import { useVerify } from '@entities/Authentication/model.ts';
import { useResetPasswordMutation } from '@entities/Authentication/rtk-query.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import YupPassword from 'yup-password';
import { doTransformRTKErrorResponse } from '@shared/helpers/index.ts';
import { useAppDispatch } from '@shared/hooks';
import { RegistrationMethod } from '@shared/types/common';
import { usePhoneVerificationIncomingModal } from '@shared/ui/modals/phone-verification-incoming-modal/helpers.ts';
import { emailRequired } from '@shared/yupValidators/validators.ts';
import { closeAuthModal, openDialogModal, openNotifyModal } from 'library/redux/modal';
import { useAuth } from '../../AuthContext';
// TODO Refactor auth to FSD
import { useLogin } from '../login-form/model';

YupPassword(yup);

interface FormValues {
  phonenumber: string;
  backend?: string;
}

type FormEmail = {
  email: string;
};

interface FormCodeValues {
  password: string;
  passwordRepeat: string;
}

const schemaCode = yup
  .object({
    passcode: yup.string().required('Это поле обязательно').min(4, 'Минимум 4 символа'),
  })
  .required();

const schemaPass = yup
  .object({
    password: yup.string().required('Заполните оба поля'),
    // .min(8, 'Пароль не может быть короче 8 символов')
    // .minLowercase(1, 'Пароль должен содержать хотя бы 1 строчная буква')
    // .minUppercase(1, 'Пароль должен содержать хотя бы 1 заглавная буква')
    // .minNumbers(1, 'Пароль должен содержать хотя бы 1 цифру')
    // .minSymbols(1, 'Пароль должен содержать хотя бы 1 специальный симвл'),
    passwordRepeat: yup
      .string()
      .required('Заполните оба поля')
      .oneOf([yup.ref('password')], 'Пароли не совпадают'),
  })
  .required();

const emailSchema = yup
  .object({
    email: emailRequired('Укажите Email'),
  })
  .required();

export const useRestore = (openLogin: () => void, openRegister: () => void) => {
  const authContext = useAuth();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1.1);
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [passcode, setPasscode] = useState<string>('');
  const [showTimer, setShowTimer] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState<boolean>(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const { onPhoneVerification, onCheckVerificationButtonClick, onEmailVerification } = useVerify();
  const [resetPassword] = useResetPasswordMutation();
  // const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { openPhoneVerificationIncomingModal } = usePhoneVerificationIncomingModal();
  // Шаг 1.1: телефон
  const {
    control: controlPhoneNumber,
    handleSubmit: submitPhone,
    reset: resetPhoneNumber,
    setError: setPhoneError,
    formState: { errors: phoneNumberErrors },
  } = useForm<FormValues>({
    defaultValues: {
      phonenumber: authContext?.state.phone ?? '',
    },
  });

  const checkedPhone = async (phonenumber: string, type: RegistrationMethod) => {
    setIsLoading(true);

    // удаляем все символы и прибавляем знак + в начале номера
    const phoneNumberToSend = `+${phonenumber.replace(/\+/, '')}`;
    await onPhoneVerification({
      type,
      phonenumber: phoneNumberToSend,
      verification_type: 'password_reset',
      captcha: captchaToken,
      onSuccess: (data) => {
        if (type === 'INCOMING_SMS') {
          openPhoneVerificationIncomingModal({
            disableOverlayCss: true,
            onSuccess: () => {
              setPhone(phoneNumberToSend);
              setStep(3);
              data?.passcode && setPasscode(data.passcode);
            },
            passcode: data?.passcode,
            verification_type: 'password_reset',
            phonenumber: phoneNumberToSend,
            number: data?.number,
          });
        } else {
          setStep(2);
          setShowTimer(true);
          setPhone(phoneNumberToSend);
        }
      },
      onError: (message) => {
        if (message === 'Такого номера нет в системе!') {
          dispatch(
            openDialogModal({
              title: 'Восстановление аккаунта',
              text: 'Данного номера нет в системе. Зарегистрироваться?',
              confirmText: 'Зарегистрироваться',
              confirm: openRegister,
              declineText: 'Нет, изменить номер',
              decline: () => resetPhoneNumber({ phonenumber: '+7' }),
            })
          );
        } else {
          setPhoneError('phonenumber', {
            type: 'value',
            message: message,
          });
        }
      },
    });
    setIsLoading(false);
  };

  // Шаг 1.2: почта
  const {
    control: controlEmail,
    handleSubmit: submitEmail,
    formState: { errors: emailErrors },
    reset: resetEmail,
    setError: setEmailError,
    clearErrors: cleanEmailErrors,
  } = useForm<FormEmail>({
    resolver: yupResolver(emailSchema),
    defaultValues: {
      email: authContext?.state.email ?? '',
    },
  });

  const checkedEmail = async (email: string) => {
    setIsLoading(true);

    // удаляем все символы и прибавляем знак + в начале номера
    await onEmailVerification({
      email,
      verification_type: 'password_reset',
      captcha: captchaToken,
      onSuccess: () => {
        setStep(2);
        setShowTimer(true);
        setEmail(email);
      },
      onError: (message) => {
        if (message === 'Такой почты нет в системе!') {
          dispatch(
            openDialogModal({
              title: 'Восстановление аккаунта',
              text: 'Данной почты нет в системе. Зарегистрироваться?',
              confirmText: 'Зарегистрироваться',
              confirm: openRegister,
              declineText: 'Нет, изменить почту',
              decline: () => resetEmail({ email: '' }),
            })
          );
        } else {
          setEmailError('email', {
            type: 'value',
            message: message,
          });
        }
      },
    });
    setIsLoading(false);
  };

  // second form - code

  const {
    control: controlPassCode,
    handleSubmit: submitCode,
    formState: { errors: passcodeErrors },
    setError,
  } = useForm<{ passcode: string }>({ resolver: yupResolver(schemaCode) });

  const checkCode = async (data: { passcode: string }) => {
    setIsLoading(true);
    await onCheckVerificationButtonClick({
      phonenumber: phone || undefined,
      email: email || undefined,
      verification_type: 'password_reset',
      passcode: data.passcode,
      onSuccess: () => {
        setStep(3);
        setPasscode(data.passcode);
      },
      onError: (message) => {
        setError('passcode', {
          type: 'value',
          message: message,
        });
      },
    });
    setIsLoading(false);
  };

  // third form - code, phone, new password
  const {
    control: passwordControl,
    handleSubmit: submitForm,
    formState: { errors: errorsPass },
    setError: setPasswordError,
    clearErrors: cleanPasswordError,
  } = useForm<{ password: string; passwordRepeat: string }>({
    resolver: yupResolver(schemaPass),
  });

  const { login } = useLogin();

  const confirmForm = async (data: FormCodeValues) => {
    setIsLoading(true);
    const response = await resetPassword({
      password: data.password,
      phonenumber: phone || undefined,
      email: email || undefined,
      passcode,
    });
    if (response?.error) {
      const transformedError = doTransformRTKErrorResponse(response.error as FetchBaseQueryError);
      const firstError = transformedError?.firstError;
      const fieldName = transformedError?.firstErrorFiledName;
      if (firstError && fieldName === 'password') {
        setPasswordError('password', {
          type: 'manual',
          message: firstError,
        });
      } else {
        setPasswordError('root', {
          type: 'manual',
          message: fieldName ? `${fieldName}: ${firstError}` : firstError,
        });
      }
    } else {
      dispatch(closeAuthModal());
      login({ phonenumber: `+${phone.replace(/\+/, '')}`, password: data.password });
      dispatch(
        openNotifyModal({
          title: ' Пароль изменен',
          text: 'Ваш пароль успешно изменен',
          confirmText: 'Ок',
        })
      );
    }
    setIsLoading(false);
  };

  const timerAction = () => {
    setShowTimer(false);
  };

  const onCaptchaChange = (token?: string) => {
    if (token) {
      setCaptchaToken(token);
      setIsCaptchaVerified(true);
    }
  };

  const openEmailForm = () => {
    setStep(1.2);
    setPhone('');
    resetPhoneNumber({ phonenumber: '' });
  };

  const openPhoneForm = () => {
    setStep(1.1);
    setEmail('');
    resetEmail({ email: '' });
  };

  const repeatEmailCodeStep = () => checkedEmail(email);

  return {
    isLoading,
    Controller,
    handleSubmit: submitPhone,
    checkedPhone,
    submitCode,
    controlPhoneNumber,
    controlPassCode,
    checkCode,
    showPassword,
    setShowPassword,
    phoneNumberErrors,
    passcodeErrors,
    passwordControl,
    submitForm,
    confirmForm,
    step,
    setStep,
    errorsPass,
    showTimer,
    timerAction,
    phone,
    // recaptchaRef,
    isCaptchaVerified,
    setIsCaptchaVerified,
    onCaptchaChange,
    openEmailForm,
    openPhoneForm,
    checkedEmail,
    controlEmail,
    submitEmail,
    emailErrors,
    cleanEmailErrors,
    email,
    repeatEmailCodeStep,
    cleanPasswordError,
    showPasswordRepeat,
    setShowPasswordRepeat,
  };
};
